import React, { useState } from 'react';
import {
  Box, Container, Typography, TextField, Button, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert, CircularProgress, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, FormControlLabel, Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { locationsApi } from '../services/api';
import { Location, LocationCreate, LocationUpdate } from '../types';

const Locations: React.FC = () => {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState<Location | null>(null);
  const [newName, setNewName] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery(['locations', 'admin'], () =>
    locationsApi.getLocations());

  const invalidate = () => {
    queryClient.invalidateQueries('locations');
  };

  const apiError = (e: any): string =>
    e?.response?.data?.detail || 'Something went wrong. Please try again.';

  const createMutation = useMutation((payload: LocationCreate) => locationsApi.createLocation(payload), {
    onSuccess: () => { invalidate(); setCreateOpen(false); setNewName(''); setErrorMsg(null); },
    onError: (e) => setErrorMsg(apiError(e)),
  });

  const updateMutation = useMutation(
    ({ id, payload }: { id: number; payload: LocationUpdate }) => locationsApi.updateLocation(id, payload),
    {
      onSuccess: () => { invalidate(); setEditOpen(false); setSelected(null); setErrorMsg(null); },
      onError: (e) => setErrorMsg(apiError(e)),
    },
  );

  const deleteMutation = useMutation((id: number) => locationsApi.deleteLocation(id), {
    onSuccess: () => { invalidate(); setDeleteOpen(false); setSelected(null); setErrorMsg(null); },
    onError: (e) => setErrorMsg(apiError(e)),
  });

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Failed to load locations. Please try again later.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">Locations</Typography>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => { setNewName(''); setErrorMsg(null); setCreateOpen(true); }}>
          Add Location
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.locations.map((loc) => (
                <TableRow key={loc.id} sx={{ opacity: loc.is_active ? 1 : 0.55 }}>
                  <TableCell>{loc.name}</TableCell>
                  <TableCell>
                    <Chip size="small" label={loc.is_active ? 'Active' : 'Inactive'}
                      color={loc.is_active ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => {
                        setSelected(loc); setErrorMsg(null); setEditOpen(true);
                      }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {loc.is_active && (
                      <Tooltip title="Deactivate">
                        <IconButton size="small" color="error"
                          onClick={() => { setSelected(loc); setErrorMsg(null); setDeleteOpen(true); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {data?.locations.length === 0 && (
                <TableRow><TableCell colSpan={3}>
                  <Alert severity="info" sx={{ my: 1 }}>No locations yet. Click "Add Location".</Alert>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Location</DialogTitle>
        <DialogContent>
          {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
          <TextField autoFocus fullWidth label="Name" value={newName} sx={{ mt: 1 }}
            placeholder="City, ST" onChange={(e) => setNewName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!newName.trim() || createMutation.isLoading}
            onClick={() => createMutation.mutate({ name: newName.trim() })}>
            {createMutation.isLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Location</DialogTitle>
        <DialogContent>
          {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
          {selected && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField fullWidth label="Name" value={selected.name}
                onChange={(e) => setSelected({ ...selected, name: e.target.value })} />
              <FormControlLabel
                control={<Switch checked={selected.is_active}
                  onChange={(e) => setSelected({ ...selected, is_active: e.target.checked })} />}
                label="Active" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!selected?.name.trim() || updateMutation.isLoading}
            onClick={() => selected && updateMutation.mutate({
              id: selected.id, payload: { name: selected.name.trim(), is_active: selected.is_active },
            })}>
            {updateMutation.isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deactivate confirmation */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Deactivate Location</DialogTitle>
        <DialogContent>
          {errorMsg && <Alert severity="error" sx={{ mb: 2 }}>{errorMsg}</Alert>}
          <Typography>
            Deactivate "{selected?.name}"? It will be hidden from dropdowns. Existing records that
            already use it keep showing it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={deleteMutation.isLoading}
            onClick={() => selected && deleteMutation.mutate(selected.id)}>
            {deleteMutation.isLoading ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Locations;
